import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Toggle } from '@components/Toggle/Toggle';

import { Controller, useForm } from 'react-hook-form';
import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { createTagCategory } from '@services/queries/tagsCategory';
import { CategoryColor } from '@models/TagCategory.model';
import {
  trackAddCategory,
  translateCategoryColor,
} from '@services/mixpanel/trackers/TagsMetrics';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Confirm,
  Line,
  Form,
  Divider,
  Label,
  Input,
  Textarea,
  Row,
  Color,
  ColorButton,
  Error,
  ToggleWithName,
  ToggleName,
} from './AddCategorieDialog.styles';

interface IAddCategorieDialog {
  open: boolean;
  onClose: () => void;
}

interface IFormData {
  name: string;
  description: string;
  color: CategoryColor;
  showOnOnboard: boolean;
}

export const AddCategorieDialog = ({ open, onClose }: IAddCategorieDialog) => {
  const portalDiv = document.getElementById('portal');

  const queryClient = useQueryClient();

  const [color, setColor] = useState<CategoryColor>(CategoryColor.BLUE);

  const schema = yup.object().shape({
    name: yup.string().required(),
    description: yup.string().required(),
    color: yup.mixed<CategoryColor>().oneOf(Object.values(CategoryColor)),
    showOnOnboard: yup.boolean().required(),
  });

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    getValues,
    formState: { isValid, errors },
    control,
    reset,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      showOnOnboard: false,
      color: CategoryColor.BLUE,
    },
  });

  const close = () => {
    reset();
    onClose();
  };

  const mutationCreateTagCategory = useMutation(createTagCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries('tagCategories');
      queryClient.invalidateQueries('tagsCategory');
      trackAddCategory(
        getValues('name'),
        !!getValues('description'),
        getValues('showOnOnboard'),
        translateCategoryColor(color),
      );
      close();
    },
    onError: (error: any) => {
      if (
        error.response?.data?.message ===
        'There is already a tag category with this name'
      ) {
        setError('name', {
          type: 'validate',
          message: 'There is already a tag category with this name',
        });
      }
    },
  });

  const onSubmit = (Tag: IFormData) => {
    if (!mutationCreateTagCategory.isLoading) {
      mutationCreateTagCategory.mutate({
        name: Tag.name,
        description: Tag.description,
        showOnOnboard: Tag.showOnOnboard,
        color,
        isUnique: true,
      });
    }
  };

  const CategoryNotSelected = () => {
    if (!color) return true;
    return false;
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Add Category</Title>
              <Close
                onClick={() => {
                  clearErrors('name');
                  close();
                }}
              >
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider height="28px" />
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Label className="mandatory">Category name</Label>

              <Input
                placeholder="Ex.: Location"
                autoFocus
                {...register('name')}
                isError={
                  errors.name &&
                  errors.name.message ===
                    'There is already a tag category with this name'
                }
              />
              {errors.name &&
                errors.name.message ===
                  'There is already a tag category with this name' && (
                  <Error>{errors.name.message}</Error>
                )}

              <Divider height="16px" />

              <Label className="mandatory">Description</Label>

              <Textarea placeholder="" {...register('description')} />

              <Divider height="16px" />

              <Label className="mandatory">Show on Onboard</Label>

              <Controller
                control={control}
                name="showOnOnboard"
                render={({ field: { onChange, value } }) => (
                  <ToggleWithName>
                    <Toggle
                      selected={value}
                      toggleSelected={() => {
                        onChange(!value);
                      }}
                    />
                    <ToggleName>{value ? 'Yes' : 'No'}</ToggleName>
                  </ToggleWithName>
                )}
              />

              <Divider height="24px" />

              <Label className="mandatory">Category Color</Label>
              <Label className="description">
                This will define the display color for all tags of this category
              </Label>

              <Divider height="16px" />

              <Row>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.BLUE);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.BLUE}
                    color={CategoryColor.BLUE}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.GREEN);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.GREEN}
                    color={CategoryColor.GREEN}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.ORANGE);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.ORANGE}
                    color={CategoryColor.ORANGE}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.PINK);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.PINK}
                    color={CategoryColor.PINK}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.RED);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.RED}
                    color={CategoryColor.RED}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.TEAL);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.TEAL}
                    color={CategoryColor.TEAL}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.VIOLET);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.VIOLET}
                    color={CategoryColor.VIOLET}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.YELLOW);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.YELLOW}
                    color={CategoryColor.YELLOW}
                  />
                </ColorButton>
              </Row>

              <Line>
                <Confirm
                  type="submit"
                  disabled={!isValid || CategoryNotSelected()}
                >
                  Confirm
                </Confirm>
              </Line>
            </Form>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
